import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/data/content";
import { scrollToId } from "@/components/SmoothScroll";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToId(id), 400);
      } else {
        scrollToId(id);
      }
    } else {
      navigate(href);
    }
  };

  const handleLogo = () => {
    setOpen(false);
    const toTop = () =>
      window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.2 }) : window.scrollTo({ top: 0, behavior: "smooth" });
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(toTop, 400);
    } else {
      toTop();
    }
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-brand-navy/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between">
        <button type="button" onClick={handleLogo} data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src="/assets/logo.png"
            alt="US Desalination"
            className={`w-auto object-contain transition-all duration-500 ${scrolled ? "h-14 md:h-16" : "h-20 md:h-24 brightness-0 invert"}`}
          />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => handleNav(l.href)}
              className={`hover-underline text-sm font-medium tracking-wide transition-colors ${
                scrolled ? "text-ink hover:text-brand-navy" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-contact-btn"
            onClick={() => handleNav("/#contact")}
            className="bg-brand-navy text-white text-sm font-semibold px-6 py-2.5 hover:bg-brand-red transition-colors duration-300"
          >
            Contact
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className={`md:hidden ${scrolled ? "text-ink" : "text-white"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-bg-dark border-t border-white/10 mt-3">
          <div className="flex flex-col px-5 py-6 gap-5">
            {NAV_LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                onClick={() => handleNav(l.href)}
                className="text-left text-white/90 text-lg font-heading"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-contact-btn"
              onClick={() => handleNav("/#contact")}
              className="bg-brand-teal text-bg-dark font-semibold px-6 py-3 mt-2"
            >
              Contact — {COMPANY.email}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
